'use client';

import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import map from 'lodash/map';
import { Lock } from 'lucide-react';
import React from 'react';

interface SidebarFloatingMenuProps {
  // The collapsed-sidebar row that opens the popout on hover. Forwarded as the
  // HoverCard trigger via Radix's asChild slot, so the row keeps its own click
  // semantics (e.g. an <a> still navigates).
  trigger: React.ReactNode;
  title: string;
  // `locked` entries keep their label but render a lock and call
  // onLockedClick instead of navigating, so the collapsed flyout matches the
  // expanded sidebar rather than silently dropping gated rows.
  items: { title: string; url: string; locked?: boolean }[];
  onLockedClick?: () => void;
}

export function SidebarFloatingMenu ({ trigger, title, items, onLockedClick }: SidebarFloatingMenuProps) {
  return (
    <HoverCardPrimitive.Root openDelay={100} closeDelay={100}>
      <HoverCardPrimitive.Trigger asChild>{trigger}</HoverCardPrimitive.Trigger>
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          side="right"
          align="start"
          sideOffset={8}
          className="z-[70] min-w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
        >
          <div className="flex h-7 items-center rounded-md px-2 text-xs font-semibold">{title}</div>
          <div className="-mx-1 my-1 h-px bg-muted" />
          <div className="flex flex-col gap-1">
            {map(items, (item) => {
              const rowClass =
                'relative flex h-7 w-full select-none items-center rounded-md px-2 text-xs outline-hidden transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus:bg-sidebar-accent focus:text-sidebar-accent-foreground';
              return item.locked ? (
                <button
                  key={item.title}
                  type="button"
                  onClick={onLockedClick}
                  className={`${rowClass} text-left`}
                >
                  {item.title}
                  <Lock className="ml-auto size-3 opacity-60" />
                </button>
              ) : (
                <a key={item.title} href={item.url} className={rowClass}>
                  {item.title}
                </a>
              );
            })}
          </div>
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}
